/**
 * V2 Localisation
 * Centralised copywriting for v2 components and pages
 */

export const v2 = {
  common: {
    close: 'Close',
    goBack: 'Go back',
    share: 'Share',
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  },
  alphaCard: {
    title: "We're in Alpha Testing!",
    subtitle: 'Help us build the best dance discovery experience for you',
    viewAll: 'View All',
    requestTitle: 'Hear what others have to say',
  },
  classPreview: {
    with: 'with',
    at: 'at',
  },
  classDetail: {
    fallbackTitle: 'Dance Class',
    linkCopied: 'Link copied to clipboard',
    favouriteFailed: 'Failed to update favourite',
    addressNotAvailable: 'Address not available',
  },
  emptyState: {
    title: 'Nothing here. For now.',
    subtitle: 'Boohoo.',
    sharePlease: 'Share please',
  },
  error: {
    loadFailed: (noun: string) => `Couldn't load ${noun}`,
    notFound: (noun: string) => `Couldn't find ${noun}`,
    crashTitle: 'Something went wrong',
    defaultTitle: 'Oops! Something went wrong',
    failureSubtitle: 'It may have been removed. Please try again.',
    notFoundSubtitle: 'It may have been removed or the link is wrong.',
    subtitle: 'Please check it and try again.',
    retry: 'Try again',
    goBack: 'Go back',
    backHome: 'Back to home',
  },
  homeNavCards: {
    packages: {
      title: 'Cross Studio\nPackages',
      subtitle: 'exclusively on TIL',
    },
    discover: {
      title: 'Discover Classes & Studios',
      subtitle: 'where you belong',
    },
    connect: {
      title: 'Connect & Share',
      subtitle: 'where you belong',
    },
  },
  contentSwitcher: {
    all: 'All',
    liked: 'Liked',
    disliked: 'Disliked',
    mine: 'Mine',
  },
  discoveryPage: {
    title: 'Discovery',
    subtitle: "Don't miss these upcoming classes!",
    tabs: {
      classes: 'Classes',
      instructors: 'Instructors',
      studios: 'Studios',
    },
    filters: {
      title: 'Filters',
      level: 'Level',
      genre: 'Genre',
      studio: 'Studio',
      clearAll: 'Clear all',
      loadFailed: "Couldn't load filters",
    },
    search: {
      placeholder: 'Search classes...',
    },
    buttons: {
      filters: 'Filters',
      favourites: 'Favourites',
      clearAll: 'Clear all',
      applyFilters: 'Apply filters',
    },
    emptyState: {
      title: 'Nothing such class. Boohoo.',
      subtitle: 'Try another search?',
      suggestListing: 'Suggest a listing',
    },
    error: {
      retry: 'Retry',
      loadMore: "Couldn't load more",
    },
    placeholder: {
      commingSoon: '{tab} coming soon',
    },
    instructors: {
      searchPlaceholder: 'Search instructors...',
      emptyState: {
        title: 'Nothing here. For now.',
        subtitle: 'Boohoo.',
      },
    },
    studios: {
      searchPlaceholder: 'Search studios...',
      emptyState: {
        title: 'Nothing here. For now.',
        subtitle: 'Boohoo.',
      },
    },
  },
  featureRequestCard: {
    likedIt: '{count} liked it',
    didntReally: "{count} didn't really",
  },
  featureRequestsPage: {
    shareYours: 'Share yours!',
    title: 'TIL. Wish Requests',
    subtitle: 'Let us know your feedbacks and things you wish to see on TIL',
    sortByVotes: 'Top',
    sortByDate: 'New',
  },
  featureRequestDetail: {
    deleteFailed: 'Failed to delete feature request',
    deleted: 'Feature request deleted',
    voteRemoved: 'Vote removed',
    voteSuccess: 'You {action} this',
    voteFailed: 'Failed to vote',
    wellYou: 'Well, you',
    wellSomeone: 'Well, someone',
    wishedFor: 'wished for',
    likedIt: 'of you liked it',
    didntLikeIt: "of you didn't quite like it",
    notFound: 'Feature request not found',
  },
  featureRequestEdit: {
    contentRequired: 'Please enter content',
    updateSuccess: 'Feature request updated',
    updateFailed: 'Failed to update feature request',
  },
  featureRequestForm: {
    incompleteFields: 'Some fields are incomplete',
    cannotLetThrough: "We can't let those through!",
    updated: 'Feature request updated',
    feedbacksAppreciated: 'Your feedbacks are greatly appreciated',
    tryAgain: 'Please try again!',
    updateFailed: "Update didn't go through. Oops",
    wishFailed: "Your Wish didn't go through. Oops",
    topic: 'Topic',
    topicPlaceholder: 'Just a one liner is enough',
    elaborate: 'Elaborate!',
    elaboratePlaceholder: 'tell us more! more is more better.',
    keepEditing: 'Keep editing',
    editedAt: 'edited {date}',
  },
  featureRequestNew: {
    title: "What's on your",
    titleAccent: 'TIL Wish List?',
    subtitle: 'We try our best lah hor.',
    exitTitle: "Abandon what you've written?",
    exitDescription: "We can't save you if you change your mind later on",
    exitConfirm: 'Abandon',
  },
  featureRequestFormEdit: {
    title: 'Edit your',
    titleAccent: 'Feature Request',
    subtitle: 'Make it better!',
    exitTitle: 'Discard your changes?',
    exitDescription: "Your changes won't be saved",
    exitConfirm: 'Discard',
  },
  homepage: {
    happeningSoon: {
      title: 'Happening Soon',
      subtitle: "Don't miss these upcoming classes!",
    },
  },
  onboarding: {
    slides: [
      {
        tag: 'DISCOVER',
        headline: 'Every dance class, one place',
        support:
          "Search, filter and browse across Singapore's studios — no more hopping between sites, lah.",
        imageAlt: 'Browsing dance classes across Singapore studios',
      },
      {
        tag: 'PASSES',
        headline: 'One pass, every studio',
        support: 'Grab a cross-studio pass once and dance across TIL studios. 90 days, your pace.',
        imageAlt: 'A single cross-studio dance pass',
      },
      {
        tag: 'COMMUNITY',
        headline: 'Better with your people',
        support: 'Reviews, a live community feed, and your favourite studios & instructors saved.',
        imageAlt: 'A dance community feed with reviews and favourites',
      },
    ],
    next: 'Next',
    start: 'Start dancing',
  },
  communityPage: {
    title: 'Community',
    subtitle: 'What is dance without community?',
    tabs: {
      all: 'All Posts',
      verified: 'Verified Posts',
    },
    empty: {
      title: 'Nothing here. For now.',
      subtitle: 'Boohoo.',
    },
    endOfFeed: "You've reached the end",
  },
  createPostPage: {
    title: "What's Brewing?",
    subtitle: 'Tell everyone all about it',
    placeholder: 'What have you been up to?',
    contentLabel: 'Post content',
    contentRequired: 'Write something before publishing.',
    charCount: '{current}/{max}',
    wordError: 'Write at least 5 words.',
    charError: 'You have reached the maximum characters',
    imagesStillUploading: 'Hang on — your images are still uploading.',
    imageSection: {
      label: 'Want Images to go along your post?',
      upload: 'Upload Image',
      uploadMore: 'Upload more Images',
      maxReached: 'You can add up to 5 images.',
      failedOverlay: 'Image failed to upload.',
    },
    verifiedCheck: {
      label: 'Select this to post with verified tag as a official entity. ',
      labelPlus: 'Once reviewed, anyone can see it.',
      disclaimer: '',
    },
    submit: 'Let it rip',
    editTitle: 'Edit your post',
    editSubtitle: 'Touch it up however you like',
    editSubmit: 'Save changes',
    toast: {
      success: 'Alright, we see you, we see you.',
      editSuccess: 'Nice — your post has been updated.',
      error: "We can't seem to process your post. {error}. Please try again!",
      imageTooLarge: 'Image must be under 5MB',
      partialImageFail: 'One of the Images failed to process',
    },
    abandon: {
      title: "Abandon what you've written?",
      description: "We can't save you if you change your mind later on",
      confirm: 'Continue',
      cancel: 'Go back',
    },
  },
} as const;
